import { dangerouslyInjectHTML } from '@visiba/react-std';
import { forwardRef, Ref } from 'react';
import styled from 'styled-components';
import type { HeadlessIconProps } from './headless_icon.component';
import { HeadlessIcon } from './headless_icon.component';
import { theme } from './icon.theme';
import { IconMap, IconType } from './icon_dictionary';
import loadedIconSet from './icon_set.json';

type IconSize = 'xSmall' | 'small' | 'medium' | 'large' | 'xLarge';

interface IconProps extends Omit<HeadlessIconProps, 'viewBox'> {
	/**
	 * The icon to use.
	 */
	type: IconType;

	/**
	 * The visual size of the icon.
	 * @default medium
	 */
	size?: IconSize;
}

const iconMap = loadedIconSet as unknown as IconMap;
const iconTypes = Object.freeze((Object.keys(iconMap) as IconType[]).sort((a, b) => a.localeCompare(b)));

export function isIconType(value: string): value is IconType {
	return iconTypes.includes(value as IconType);
}

const StyledIcon = styled(HeadlessIcon)`
	--width: auto;
	--height: auto;

	width: var(--width);
	height: var(--height);

	&[data-size='xSmall'] {
		--width: ${theme.getValue('xSmallSize')};
		--height: ${theme.getValue('xSmallSize')};
	}

	&[data-size='small'] {
		--width: ${theme.getValue('smallSize')};
		--height: ${theme.getValue('smallSize')};
	}
	&[data-size='medium'] {
		--width: ${theme.getValue('mediumSize')};
		--height: ${theme.getValue('mediumSize')};
	}
	&[data-size='large'] {
		--width: ${theme.getValue('largeSize')};
		--height: ${theme.getValue('largeSize')};
	}
	&[data-size='xLarge'] {
		--width: ${theme.getValue('xLargeSize')};
		--height: ${theme.getValue('xLargeSize')};
	}
`;

/**
 * Icon component for all icons used
 */
function CellulaIcon({ type, size = 'medium', ...rest }: IconProps, ref: Ref<SVGSVGElement>): JSX.Element {
	return (
		<StyledIcon
			{...rest}
			ref={ref}
			viewBox={iconMap[type]?.viewBox}
			dangerouslySetInnerHTML={dangerouslyInjectHTML(iconMap[type]?.innerHTML)}
			data-size={size}
		/>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for displaying icons.
 */
const Icon = forwardRef(CellulaIcon);

export type { IconProps };
export { Icon, iconTypes };
